import { Link2, Store } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import clsx from "clsx";
import { Icon, type IconSize } from "../Icon/Icon.js";
import { PLATFORM_MARKS } from "./marks.js";
import { platformLabel } from "./platforms.js";
import styles from "./PlatformIcon.module.scss";

/** Platforms with no vendored mark fall back to a Lucide glyph rather than to nothing. */
const FALLBACKS: Record<string, LucideIcon> = {
  fourthwall: Store, // not in Simple Icons at all; a storefront glyph beats the generic link chain
};

export interface PlatformIconProps {
  /** A `PLATFORM_MARKS` key — either a `PLATFORMS` platform or an `integrations.connections.provider`.
   *  Anything unrecognised (including null) renders the generic link glyph. */
  platform: string | null | undefined;
  size?: IconSize;
  /** Accessible name for a standalone glyph; omit inside a row that already names the link. */
  label?: string;
  className?: string;
}

/**
 * A platform's brand mark, in that platform's `--ac-color-p-*` accent.
 *
 * These are the ONE exception to the guide's "Lucide outline, 1.5px stroke" rule (STYLE-GUIDE ruling
 * 11): a brand mark is a filled logo whose shape is not ours to restyle, and Lucide deliberately
 * ships none. Anything with no vendored mark — Custom, plus the FALLBACKS above — renders a normal Lucide
 * glyph instead, so the exception stays exactly as wide as it has to be.
 */
export function PlatformIcon({ platform, size = 20, label, className }: PlatformIconProps) {
  const key = platform ?? "custom";
  const mark = PLATFORM_MARKS[key];
  const name = label ?? platformLabel(platform);

  if (!mark) {
    return (
      <span className={clsx(styles.icon, className)} data-platform={key}>
        <Icon icon={FALLBACKS[key] ?? Link2} size={size} label={label} />
      </span>
    );
  }
  return (
    <svg
      className={clsx(styles.icon, styles.mark, className)}
      data-platform={key}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role={label ? "img" : "presentation"}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label && <title>{name}</title>}
      <path d={mark} fill="currentColor" />
    </svg>
  );
}
